import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import useSite from './use-site'

const FLOOR_STATUS_IGNORED_DEVICE_TYPES = ['tv', 'chromecast']

const hardwareTypeToModelType = type => {
  if (type === 'access_points') {
    return 'ap'
  } else if (type === 'switches') {
    return 'switch'
  } else if (type === 'esls') {
    return 'esl'
  } else if (type === 'chromecasts') {
    return 'chromecast'
  } else if (type === 'tvs') {
    return 'tv'
  }
}

const sortFloors = floors => {
  return floors.sort((a, b) => {
    const levelA = parseInt(a.level)
    const levelB = parseInt(b.level)
    return (!isNaN(levelA) ? levelA : 0) - (!isNaN(levelB) ? levelB : 0)
  })
}

const useBuildings = (focusId) => {
  const { hardwareType } = useParams()
  const { loading, site, equipments } = useSite(focusId)

  const buildings = useMemo(() => {
    if (site) {
      const isSiteDown = site.equipment?.available_level === 'down'
      
      return site.surface_areas.map(({ maps, ...building }) => ({
        ...building,
        floors: sortFloors([...maps]).map(floor => {
          if (isSiteDown) {
            return {
              ...floor,
              available_level: 'unknown'
            }
          }

          const floorStatusEquipments = equipments
            .filter(equipment =>
              equipment.floor &&
              floor.uuid === equipment.floor.uuid &&
              !FLOOR_STATUS_IGNORED_DEVICE_TYPES.includes(equipment.model?.type)
            )

          const nbDown = floorStatusEquipments
            .reduce((acc, equipment) => {
              if (equipment.available_level === 'down') {
                return acc + 1
              }
              return acc
            }, 0)

          const totalEquipments = floorStatusEquipments.length

          let available_level = 'up'
          
          if (nbDown > 0) {
            available_level = nbDown === totalEquipments ? 'down' : 'degraded'
          }

          return {
            ...floor,
            available_level
          }
        })
      }))
    }
    return []
  }, [site, equipments])

  const findFirstFloor = (targetBuildingId) => {
    const types = [
      hardwareType ? hardwareTypeToModelType(hardwareType) : null,
      'ap',
      'switch',
      'esl',
      'chromecast',
      'tv'
    ].filter(Boolean)
    const devices = equipments.filter(({ model, position }) => position && model && types.includes(model.type))

    if (targetBuildingId) {
      const targetBuilding = buildings.find(b => b.uuid === targetBuildingId)
      if (targetBuilding && targetBuilding.floors.length > 0) {
        return {
          building: targetBuilding,
          floor: targetBuilding.floors[0]
        }
      }
    }

    let firstFloor, firstBuilding
    for (let building of buildings) {
      const floor = building.floors.find(
        ({ uuid }) => devices.findIndex(({ position }) => position?.map?.uuid === uuid) > -1
      ) || building.floors[0]

      if (floor) {
        firstFloor = floor
        firstBuilding = building
        break
      }
    }

    if (!firstFloor) {
      return
    }
    return {
      building: firstBuilding,
      floor: firstFloor
    }
  }

  return {
    loading,
    buildings,
    site,
    findFirstFloor,
    redirect: (targetBuildingId) => {
      const { floor, building } = findFirstFloor(targetBuildingId) || {}
      if (!floor || !building) {
        return `/monitoring/map/${focusId}/no_surface_areas`
      }
      return `/monitoring/map/${focusId}/${building.uuid}/${floor.uuid || ''}`
    }
  }
}

export default useBuildings
